import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DealType } from '../../../enums/essential-ui/EssentialDealType';
import { VehicleType } from '../../../enums/essential-ui/EssentialVehicleType';
import { OfferType } from '../../../enums/essential-ui/OfferType';
import { SortBy } from '../../../enums/essential-ui/SortBy';
import { CampaignActivityFilter } from '../../../types/CampaignActivityFilter';
import { MinMaxFilter } from '../../../types/essential-ui/MinMaxFilter';

export type FiltersState = {
  offerType: OfferType;
  dealTypes: readonly DealType[];
  sortBy: SortBy;
  campaignActivitiesFilter: CampaignActivityFilter;
  bps: MinMaxFilter;
  remainingPayments: MinMaxFilter;
  lastUpdate: MinMaxFilter;
  years: readonly number[];
  incentives: readonly string[];
  privateIncentives: readonly string[];
  brands: readonly string[];
  models: readonly string[];
  trims: readonly string[];
  soldByList: readonly string[];
  vehicleTypes: readonly VehicleType[];
};

export const initialFiltersState: FiltersState = {
  dealTypes: [DealType.Lease, DealType.Finance, DealType.Cash],
  offerType: OfferType.AllDeals,
  sortBy: SortBy.BPS_HIGHEST,
  campaignActivitiesFilter: {
    zeroTimesOpened: true,
    oneTimeOpened: true,
    twoOrMoreTimesOpened: true,
  },
  bps: {
    min: 0,
    max: 100,
  },
  remainingPayments: {
    min: 0,
    max: 100,
  },
  lastUpdate: {
    min: 0,
    max: 9999,
  },
  years: [],
  incentives: [],
  privateIncentives: [],
  models: [],
  trims: [],
  brands: [],
  soldByList: [],
  vehicleTypes: [VehicleType.New, VehicleType.PreOwned],
};

const filterSlice = createSlice({
  name: 'filters',
  initialState: initialFiltersState,
  reducers: {
    setOfferType(state, action: PayloadAction<OfferType>) {
      state.offerType = action.payload;
    },
    setDealTypes(state, action: PayloadAction<DealType[]>) {
      state.dealTypes = action.payload;
    },
    setBrands(state, action: PayloadAction<string[]>) {
      state.brands = action.payload;
    },
    setSortBy(state, action: PayloadAction<SortBy>) {
      state.sortBy = action.payload;
    },
    setCampaignActivity(state, action: PayloadAction<CampaignActivityFilter>) {
      state.campaignActivitiesFilter = action.payload;
    },
    setIncentive(state, action: PayloadAction<string[]>) {
      state.incentives = action.payload;
    },
    setPrivateIncentive(state, action: PayloadAction<string[]>) {
      state.privateIncentives = action.payload;
    },
    setSoldByList(state, action: PayloadAction<string[]>) {
      state.soldByList = action.payload;
    },
    setBps(state, action: PayloadAction<MinMaxFilter>) {
      state.bps = action.payload;
    },
    setRemainingPayments(state, action: PayloadAction<MinMaxFilter>) {
      state.remainingPayments = action.payload;
    },
    setLastUpdate(state, action: PayloadAction<MinMaxFilter>) {
      state.lastUpdate = action.payload;
    },
    filtersReset(state) {
      return {
        ...state,
        dealTypes: initialFiltersState.dealTypes,
        offerType: initialFiltersState.offerType,
        sortBy: initialFiltersState.sortBy,
        campaignActivitiesFilter: initialFiltersState.campaignActivitiesFilter,
        bps: initialFiltersState.bps,
        remainingPayments: initialFiltersState.remainingPayments,
        lastUpdate: initialFiltersState.lastUpdate,
        vehicleTypes: initialFiltersState.vehicleTypes,
      };
    },
    setYears(state, action: PayloadAction<number[]>) {
      state.years = action.payload;
    },
    setModels(state, action: PayloadAction<string[]>) {
      state.models = action.payload;
    },
    setTrims(state, action: PayloadAction<string[]>) {
      state.trims = action.payload;
    },
    setVehicleTypes(state, action: PayloadAction<VehicleType[]>) {
      state.vehicleTypes = action.payload;
    },
  },
});

export const {
  setOfferType,
  setDealTypes,
  setBrands,
  setSortBy,
  setCampaignActivity,
  setIncentive,
  setPrivateIncentive,
  setSoldByList,
  setBps,
  setRemainingPayments,
  setLastUpdate,
  filtersReset,
  setYears,
  setModels,
  setTrims,
  setVehicleTypes,
} = filterSlice.actions;

export default filterSlice.reducer;
